import { cn } from '@/utils'
import { useCallback, useMemo, useState } from 'react'

import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { Checkbox } from '@/components/ui/checkbox'
import { HidePartButton } from '@/pages/CADPage/components/AssemblyTree/HidePartButton'

import { SelectionEvent, useSelectable } from '../../hooks'
import type { GLTFObject } from '@/lib/cad/GLTFObject'
import { type ASMTreeNode } from '@/state'
import { EditableField } from '@/components/core/EditableField'
import {
  useAssemblyTreeQuery,
  useUpdateCadVersionMutation,
} from '../../queries'
import { useCADQuery } from '@/services/queries/cads'
import { useCADPageStore } from '@/state/cad'

export const StepPart = ({
  part,
  gltf,
  isSelected,
  selectFromCad,
  onMouseDown,
  onDelete,
  setIsEditing,
  isReadOnly,
  selectStep,
}: {
  part: ASMTreeNode
  gltf: GLTFObject
  stepIsActive: boolean
  isSelected: boolean
  selectFromCad: boolean
  onMouseDown: (e: SelectionEvent) => void
  onDelete: (partsUuids: Array<string>) => void
  setIsEditing: (isEditing: boolean) => void
  isReadOnly?: boolean
  selectStep: () => void
}) => {
  const selectable = useSelectable({})
  const [isHiddenButtonVisible, setIsHiddenButtonVisible] = useState(false)
  const {
    data: { assemblyTree },
  } = useAssemblyTreeQuery()
  const { data: cadData } = useCADQuery()
  const { mutateAsync: updateCadVersion } = useUpdateCadVersionMutation()

  const hiddenParts = useCADPageStore((state) => state.hiddenParts)
  const isPartHidden = useMemo(
    () => hiddenParts.includes(part.uuid),
    [hiddenParts, part.uuid],
  )

  const handleCheckboxClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()

      if (isSelected) {
        // Deselect the part
        const newSelected = selectable.selected.filter(
          (uuid) => uuid !== part.uuid,
        )
        useCADPageStore.getState().setState({ selectedParts: newSelected })
        if (newSelected.length === 0) {
          useCADPageStore.getState().setState({ selectionContext: null })
        }
      } else {
        // Select the part
        useCADPageStore.getState().setState({
          selectedParts: [...selectable.selected, part.uuid],
          selectionContext: 'step',
        })
      }

      // Select the step
      selectStep()
    },
    [isSelected, selectable, part.uuid, selectStep],
  )

  const cadVersionId = useMemo(
    () => cadData?.version?.id as string,
    [cadData?.version],
  )
  const documentVersionId = useMemo(
    () => cadData?.documentVersion?.id as string,
    [cadData?.documentVersion],
  )

  const handlePartRename = useCallback(
    async (partName: string) => {
      if (!assemblyTree) return

      // Deep clone to create new array reference
      const tree = {
        ...assemblyTree,
        nodes: assemblyTree.nodes.map((n) =>
          n.uuid === part.uuid ? { ...n, display_name: partName } : n,
        ),
      }

      if (assemblyTree && cadVersionId && documentVersionId) {
        await updateCadVersion({
          cadVersionId,
          documentVersionId,
          assemblyTree: tree,
        })
      }
    },
    [assemblyTree, part, cadVersionId, documentVersionId, updateCadVersion],
  )

  return (
    <ContextMenu>
      <ContextMenuTrigger
        disabled={isReadOnly}
        onMouseDown={onMouseDown}
        onMouseOver={(e) => {
          e.stopPropagation()
          setIsHiddenButtonVisible(true)
          selectable
            .handlers(part.uuid, 'step', { internalView: true })
            .onSelectHover(e)
        }}
        onMouseOut={(e) => {
          e.stopPropagation()
          setIsHiddenButtonVisible(false)
          selectable.onSelectEnd(e)
        }}
        onMouseUp={(e) => {
          e.stopPropagation()
          selectable.onSelectEnd(e)
        }}
      >
        <div
          id={`step-part-diplay-name-${part.uuid}`}
          className={cn(
            'flex flex-1 items-center justify-between font-semibold py-1 hover:bg-primary-20 cursor-pointer pr-3',
            {
              'bg-primary-20': isSelected,
            },
            // blend selected parts with active step in selectFromCAD mode
            {
              'bg-purple-50': isSelected && selectFromCad,
            },
            // set cursor to disabled when part is already attached to step
            {
              'hover:bg-purple-80 cursor-not-allowed':
                selectFromCad && isSelected,
            },
            {
              'hover:bg-white cursor-not-allowed': selectFromCad && !isSelected,
            },
            {
              'text-gray-400': isPartHidden,
            },
          )}
          data-testid="step-part"
          onClick={(e) => {
            selectStep()
            e.stopPropagation()
          }}
        >
          <div className="flex items-center flex-1 min-w-0">
            <div onClick={(e) => e.stopPropagation()}>
              {isHiddenButtonVisible || isSelected ? (
                <Checkbox
                  checked={isSelected}
                  onMouseDown={handleCheckboxClick}
                  className="ml-1 flex-shrink-0"
                />
              ) : (
                <div className="w-4 h-4 ml-1 flex-shrink-0"></div>
              )}
            </div>
            {isReadOnly ? (
              <div className={cn('px-4', { 'text-gray-400': isPartHidden })}>
                {part.display_name}
              </div>
            ) : (
              <EditableField
                value={part.display_name}
                onSaveHandler={(name) => {
                  void handlePartRename(name)
                }}
                textClassNames={cn('pl-[20px]', {
                  'text-gray-400': isPartHidden,
                })}
                inputClassNames="ml-[20px]"
                onEdit={(isEditingNode) => {
                  setIsEditing(isEditingNode)
                }}
              />
            )}
          </div>

          <div onClick={(e) => e.stopPropagation()}>
            <HidePartButton
              node={part}
              gltf={gltf}
              isVisible={isHiddenButtonVisible}
            />
          </div>
        </div>
      </ContextMenuTrigger>
      {!isReadOnly && !selectFromCad && (
        <ContextMenuContent>
          <ContextMenuItem
            onMouseDown={(e) => {
              e.stopPropagation()
              onDelete([part.uuid])
            }}
          >
            Remove
          </ContextMenuItem>
        </ContextMenuContent>
      )}
    </ContextMenu>
  )
}
